import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { FormData } from '@/types';
import { FormAnswer as ClientFormAnswer } from '@/types';
import { useStore, formActions, FormAnswer } from '@/lib/store';
import { formService } from '@/lib/services';

// Helper to convert client FormAnswer to store FormAnswer
const convertToStoreFormAnswer = (formId: string, data: FormData): FormAnswer => ({
  id: formId,
  userId: data.userId as string,
  answers: {
    userType: data.userType,
    excludedSituation: data.excludedSituation,
    propertyOwnership: data.propertyOwnership,
    singleOwnership: data.singleOwnership,
    spouseOwnership: data.spouseOwnership,
    leaseTermType: data.leaseTermType,
    leaseDuration: data.leaseDuration,
    buildingType: data.buildingType,
    buildingAgeUnder5: data.buildingAgeUnder5,
    tenant65Over: data.tenant65Over,
    additionalInfo: data.additionalInfo,
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

// Helper to convert client FormAnswer to store FormAnswer
const convertClientToStoreFormAnswer = (clientForm: ClientFormAnswer, formId: string): FormAnswer => ({
  id: formId,
  userId: clientForm.userId,
  answers: {
    userType: clientForm.userType,
    excludedSituation: clientForm.excludedSituation,
    propertyOwnership: clientForm.propertyOwnership,
    singleOwnership: clientForm.singleOwnership,
    spouseOwnership: clientForm.spouseOwnership,
    leaseTermType: clientForm.leaseTermType,
    leaseDuration: clientForm.leaseDuration,
    buildingType: clientForm.buildingType,
    buildingAgeUnder5: clientForm.buildingAgeUnder5,
    tenant65Over: clientForm.tenant65Over,
    additionalInfo: clientForm.additionalInfo,
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const useFormStore = () => {
  const { data: session } = useSession();
  const store = useStore();
  const queryClient = useQueryClient();
  const {
    setFormData,
    setFormAnswerId,
    setIsSubmitting,
    setIsLoading,
    setError,
    setQuestionnaireSubmitted,
    setQuestionnaireSubmission,
    resetForm
  } = formActions;

  // Use React Query for form data
  const { data: formQueryData, isLoading: isQueryLoading } = useQuery({
    queryKey: ['form-data', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      const response = await formService.getFormAnswer(session);
      if (response) {
        const formId = response.id;
        const storeFormData = convertClientToStoreFormAnswer(response, formId);
        setFormData(storeFormData);
        setFormAnswerId(formId);
        setQuestionnaireSubmitted(true);
        setQuestionnaireSubmission('complete');
        localStorage.setItem('formAnswerId', formId);
        return storeFormData;
      }
      // Reset form state when no form is found
      setFormData(null);
      setFormAnswerId('');
      setQuestionnaireSubmitted(false);
      setQuestionnaireSubmission('none');
      localStorage.removeItem('formAnswerId');
      return null;
    },
    enabled: !!session?.user?.id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const submitForm = useCallback(async (formData: FormData) => {
    if (!session?.user?.id || !session?.user?.accessToken) {
      toast.error('Please log in to continue');
      return null;
    }

    setIsSubmitting(true);
    try {
      const formId = await formService.createFormAnswer(formData, session);
      if (formId) {
        const storeFormData = convertToStoreFormAnswer(formId, formData);
        setFormAnswerId(formId);
        setFormData(storeFormData);
        setQuestionnaireSubmitted(true);
        setQuestionnaireSubmission('complete');
        localStorage.setItem('formAnswerId', formId);
        // Invalidate form data query to trigger a refresh
        queryClient.invalidateQueries({ queryKey: ['form-data'] });
        return storeFormData;
      }
      return null;
    } catch (error) {
      setError(error as Error);
      toast.error('Failed to submit form');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [session, queryClient, setFormData, setIsSubmitting, setFormAnswerId, setQuestionnaireSubmitted, setQuestionnaireSubmission, setError]);

  const updateForm = useCallback(async (formData: FormData, formAnswerId: string) => {
    if (!session?.user?.id || !session?.user?.accessToken) {
      toast.error('Please log in to continue');
      return false;
    }

    setIsSubmitting(true);
    try {
      const success = await formService.updateFormAnswer(formData, formAnswerId, session);
      if (success) {
        const storeFormData = convertToStoreFormAnswer(formAnswerId, formData);
        setFormData(storeFormData);
        // Invalidate form data query to trigger a refresh
        queryClient.invalidateQueries({ queryKey: ['form-data'] });
      }
      return success;
    } catch (error) {
      setError(error as Error);
      toast.error('Failed to update form');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [session, queryClient, setFormData, setIsSubmitting, setError]);

  // Simplified fetchFormData that just triggers a query refresh
  const fetchFormData = useCallback(async () => {
    if (!session?.user?.id) return null;
    await queryClient.invalidateQueries({ queryKey: ['form-data'] });
    return formQueryData;
  }, [session?.user?.id, queryClient, formQueryData]);

  return {
    formData: store.form.formData,
    formAnswerId: store.form.formAnswerId,
    isSubmitting: store.form.isSubmitting,
    isLoading: store.form.isLoading || isQueryLoading,
    error: store.form.error,
    isQuestionnaireSubmitted: store.form.isQuestionnaireSubmitted,
    questionnaireSubmission: store.form.questionnaireSubmission,
    submitForm,
    updateForm,
    fetchFormData,
    resetForm,
    setQuestionnaireSubmitted,
    setQuestionnaireSubmission,
    setFormAnswerId
  };
}; 